import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Paper,
  Card,
  Chip,
  Skeleton,
  keyframes,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { Title, useDataProvider, useNotify } from 'react-admin';
import { DynamicDataGrid, ColumnDefinition } from '../../components';

// Magical shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Sparkle float animation
const sparkleFloat = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: translateY(-10px) rotate(180deg);
    opacity: 0.5;
  }
`;

// Pulse glow animation
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(120, 105, 223, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(120, 105, 223, 0.6), 0 0 30px rgba(102, 126, 234, 0.4);
  }
`;

// Magic wand wave animation
const wandWave = keyframes`
  0% {
    transform: rotate(-10deg) scale(1);
  }
  25% {
    transform: rotate(10deg) scale(1.1);
  }
  50% {
    transform: rotate(-5deg) scale(1);
  }
  75% {
    transform: rotate(5deg) scale(1.05);
  }
  100% {
    transform: rotate(-10deg) scale(1);
  }
`;

// Helper functions for field type detection (matching DynamicDataGrid)
const isVolumeField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('litro') ||
    lowerField.includes('liter') ||
    lowerField.includes('volume') ||
    lowerField === 'amount' ||
    lowerField.includes('_amount') ||
    lowerField.includes('amount_') ||
    lowerField === 'quantidade' ||
    lowerField.includes('total_amount') ||
    lowerField.includes('average_amount')
  );
};

const isUnitPriceField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('price_per_liter') ||
    lowerField.includes('preco_por_litro') ||
    lowerField.includes('unit_price') ||
    lowerField.includes('preco_unitario') ||
    lowerField.includes('old_price_per_liter') ||
    lowerField.includes('average_price')
  );
};

const isPointsField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('ponto') ||
    lowerField.includes('point') ||
    lowerField.includes('score') ||
    lowerField.includes('saldo') ||
    lowerField.includes('balance')
  );
};

const isCurrencyField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  if (isVolumeField(field)) return false;
  if (isPointsField(field)) return false;
  return (
    lowerField.includes('valor') ||
    lowerField.includes('value') ||
    lowerField.includes('price') ||
    lowerField.includes('total') ||
    lowerField.includes('preco') ||
    lowerField.includes('desconto') ||
    lowerField.includes('discount') ||
    lowerField.includes('faturamento') ||
    lowerField.includes('ticket')
  );
};

const isCountField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('count') ||
    lowerField.includes('quantidade') ||
    lowerField.includes('qtd') ||
    lowerField.includes('total_') ||
    lowerField.includes('num_')
  );
};

interface SummaryItem {
  label: string;
  value: string;
  type: 'total' | 'average' | 'count';
}

interface SummaryStatsProps {
  data: Record<string, any>[];
  columns: ColumnDefinition[];
}

// Summary Statistics Component
const SummaryStats: React.FC<SummaryStatsProps> = ({ data, columns }) => {
  const summaryItems: SummaryItem[] = React.useMemo(() => {
    if (!data || data.length === 0 || !columns) return [];

    const items: SummaryItem[] = [];

    // Always add record count
    items.push({
      label: 'Total de Registros',
      value: data.length.toLocaleString('pt-BR'),
      type: 'count',
    });

    // Process numeric columns
    columns.forEach((column) => {
      if (column.type !== 'number') return;

      const field = column.field;
      const values = data
        .map((row) => row[field])
        .filter((v) => v !== null && v !== undefined && typeof v === 'number');

      if (values.length === 0) return;

      const sum = values.reduce((acc, val) => acc + val, 0);
      const avg = sum / values.length;

      // Determine what kind of summary to show based on field type
      if (isVolumeField(field)) {
        items.push({
          label: `Total ${column.headerName}`,
          value: sum.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + ' L',
          type: 'total',
        });
      } else if (isUnitPriceField(field)) {
        // For unit prices, show average instead of total
        items.push({
          label: `Média ${column.headerName}`,
          value: avg.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          }),
          type: 'average',
        });
      } else if (isPointsField(field)) {
        items.push({
          label: `Total ${column.headerName}`,
          value: sum.toLocaleString('pt-BR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' pts',
          type: 'total',
        });
      } else if (isCurrencyField(field)) {
        items.push({
          label: `Total ${column.headerName}`,
          value: sum.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          type: 'total',
        });
      } else if (isCountField(field)) {
        items.push({
          label: `Total ${column.headerName}`,
          value: sum.toLocaleString('pt-BR'),
          type: 'total',
        });
      }
    });

    return items;
  }, [data, columns]);

  if (summaryItems.length <= 1) return null; // Only has record count, no numeric fields

  return (
    <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', mb: 2 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {summaryItems.map((item, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    bgcolor: '#7869DF',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {item.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {summaryItems.map((item, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    textAlign: 'center',
                    color: item.type === 'count' ? '#666' : '#333',
                  }}
                >
                  {item.value}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Magical Loading Skeleton Component
const MagicalLoadingSkeleton: React.FC = () => {
  const columns = [1, 2, 3, 4, 5];
  const rows = [1, 2, 3, 4, 5, 6];

  return (
    <Card sx={{ overflow: 'hidden', position: 'relative', animation: `${pulseGlow} 2s ease-in-out infinite` }}>
      {/* Floating sparkles */}
      {[...Array(6)].map((_, i) => (
        <SparkleIcon
          key={i}
          sx={{
            position: 'absolute',
            color: '#667eea',
            fontSize: 16,
            opacity: 0.6,
            animation: `${sparkleFloat} ${1.5 + i * 0.3}s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
            top: `${15 + (i * 15) % 70}%`,
            left: `${10 + (i * 17) % 80}%`,
            zIndex: 10,
          }}
        />
      ))}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          bgcolor: '#7869DF',
          p: 1,
        }}
      >
        {columns.map((col) => (
          <Box key={col} sx={{ flex: 1, px: 1 }}>
            <Skeleton
              variant="text"
              width={`${60 + Math.random() * 30}%`}
              height={24}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&::after': {
                  background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)`,
                  animation: `${shimmer} 1.5s infinite`,
                },
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Rows */}
      {rows.map((row) => (
        <Box
          key={row}
          sx={{
            display: 'flex',
            p: 1,
            borderBottom: '1px solid #f0f0f0',
            bgcolor: row % 2 === 0 ? '#fafafa' : 'white',
          }}
        >
          {columns.map((col) => (
            <Box key={col} sx={{ flex: 1, px: 1 }}>
              <Skeleton
                variant="text"
                width={`${40 + Math.random() * 50}%`}
                height={20}
                sx={{
                  background: `linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)`,
                  backgroundSize: '200% 100%',
                  animation: `${shimmer} 1.5s infinite`,
                  animationDelay: `${row * 0.1}s`,
                }}
              />
            </Box>
          ))}
        </Box>
      ))}

      {/* Magic wand icon with text */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          zIndex: 20,
          bgcolor: 'rgba(255,255,255,0.95)',
          borderRadius: 3,
          p: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
        }}
      >
        <Box
          sx={{
            animation: `${wandWave} 2s ease-in-out infinite`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <SparkleIcon sx={{ fontSize: 32, color: '#667eea' }} />
          <BotIcon sx={{ fontSize: 40, color: '#7869DF' }} />
          <SparkleIcon sx={{ fontSize: 32, color: '#667eea' }} />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: '#7869DF',
            fontWeight: 600,
            background: 'linear-gradient(90deg, #667eea, #7869DF, #667eea)',
            backgroundSize: '200% 100%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${shimmer} 2s infinite`,
          }}
        >
          Gerando relatório com IA...
        </Typography>
        <Typography variant="caption" sx={{ color: '#999' }}>
          Analisando sua solicitação
        </Typography>
      </Box>
    </Card>
  );
};

interface ReportFilter {
  label: string;
  value: string;
}

interface ReportData {
  success: boolean;
  data: Record<string, any>[];
  columns: ColumnDefinition[];
  filters?: ReportFilter[];
  rowCount?: number;
  error?: string;
  warning?: string;
}

const QUERY_MAX_LENGTH = 300;

interface InputCardProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isLoading: boolean;
  centered?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

const InputCard: React.FC<InputCardProps> = React.memo(({
  value,
  onChange,
  onSend,
  isLoading,
  centered = false,
  inputRef,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        maxWidth: centered ? 600 : '100%',
        borderRadius: '28px',
        bgcolor: 'white',
        display: 'flex',
        alignItems: 'center',
        px: 2,
      }}
    >
      <TextField
        fullWidth
        placeholder="Pergunte alguma coisa... Ex: 'Mostre os 10 clientes que mais abasteceram'"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        disabled={isLoading}
        multiline
        maxRows={10}
        variant="standard"
        inputProps={{ maxLength: QUERY_MAX_LENGTH }}
        InputProps={{
          disableUnderline: true,
        }}
        helperText={`${value.length}/${QUERY_MAX_LENGTH}`}
        FormHelperTextProps={{
          sx: {
            textAlign: 'right',
            color: value.length >= QUERY_MAX_LENGTH ? '#d32f2f' : '#9e9e9e',
          },
        }}
        sx={{
          '& .MuiInputBase-root': {
            py: 1.5,
          },
          '& .MuiInputBase-input': {
            '&::placeholder': {
              color: '#9e9e9e',
              opacity: 1,
            },
          },
        }}
      />
      <IconButton
        onClick={onSend}
        disabled={!value.trim() || isLoading}
        size="small"
        sx={{
          color: 'white',
          bgcolor: value.trim() ? '#667eea' : '#e0e0e0',
          borderRadius: '50%',
          width: 36,
          height: 36,
          ml: 1,
          '&:hover': {
            bgcolor: value.trim() ? '#764ba2' : '#e0e0e0',
          },
          '&.Mui-disabled': {
            color: 'white',
            bgcolor: '#e0e0e0',
          },
        }}
      >
        {isLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
      </IconButton>
    </Paper>
  );
});

InputCard.displayName = 'InputCard';

const ReportAI: React.FC = () => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [lastQuery, setLastQuery] = useState<string>('');
  const [hasQueried, setHasQueried] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendQuery = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const queryText = input;
    setLastQuery(queryText);
    setInput('');
    setIsLoading(true);
    setHasQueried(true);
    setReportData(null);

    try {
      const response = await dataProvider.create('chains/chainId/report-ai/query', {
        data: { query: queryText },
      });

      const result = response.data as ReportData;

      if (result.success) {
        setReportData(result);
      } else {
        setReportData({
          success: false,
          data: [],
          columns: [],
          error: result.error || 'Não foi possível processar sua solicitação.',
        });
        notify(result.error || 'Erro ao processar consulta', { type: 'warning' });
      }
    } catch (error) {
      console.error('Error sending query:', error);
      setReportData({
        success: false,
        data: [],
        columns: [],
        error: 'Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.',
      });
      notify('Erro ao processar solicitação', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, dataProvider, notify]);

  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Title title="Relatório IA" />

      {!hasQueried ? (
        // Initial View - Centered layout
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            gap: 3,
          }}
        >
          <BotIcon sx={{ fontSize: 64, color: '#667eea' }} />

          <Typography
            variant="h4"
            sx={{
              color: '#333',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            Como posso ajudar?
          </Typography>

          <InputCard
            value={input}
            onChange={handleInputChange}
            onSend={sendQuery}
            isLoading={isLoading}
            centered
            inputRef={inputRef}
          />

          <Typography
            variant="caption"
            sx={{
              color: '#9e9e9e',
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            Use linguagem natural para solicitar relatórios. Ex: "Mostre os 10 clientes que mais abasteceram"
          </Typography>
        </Box>
      ) : (
        // Report View - Input at top, DataGrid below
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Input Area - Top */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'white',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <BotIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Box sx={{ flex: 1 }}>
              <InputCard
                value={input}
                onChange={handleInputChange}
                onSend={sendQuery}
                isLoading={isLoading}
                inputRef={inputRef}
              />
            </Box>
          </Box>

          {/* Last Query Info and Filters */}
          {lastQuery && (
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', mx: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  <strong>Consulta:</strong> {lastQuery}
                </Typography>
                {reportData && reportData.success && (
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    <strong>Resultados:</strong> {reportData.rowCount || reportData.data?.length || 0} registro(s)
                  </Typography>
                )}
              </Box>
              {/* Filters */}
              {reportData?.filters && reportData.filters.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#666', mr: 0.5 }}>
                    <strong>Regras aplicadas:</strong>
                  </Typography>
                  {reportData.filters.map((filter, index) => (
                    <Chip
                      key={index}
                      label={`${filter.label}: ${filter.value}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#667eea',
                        color: '#667eea',
                        bgcolor: 'rgba(102, 126, 234, 0.08)',
                        cursor: 'default',
                        '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' },
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              {/* Warning about period limitation */}
              {reportData?.warning && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 1.5,
                    '& .MuiAlert-icon': {
                      color: '#667eea',
                    },
                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                    color: '#555',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                  }}
                >
                  {reportData.warning}
                </Alert>
              )}
            </Box>
          )}

          {/* Content Area */}
          <Box sx={{ flex: 1, p: 2 }}>
            {isLoading ? (
              // Magical Loading State
              <MagicalLoadingSkeleton />
            ) : reportData ? (
              reportData.success && reportData.data && reportData.data.length > 0 ? (
                // Summary + DataGrid
                <>
                  <SummaryStats data={reportData.data} columns={reportData.columns} />
                  <Card>
                    <DynamicDataGrid
                      data={reportData.data}
                      columns={reportData.columns}
                    />
                  </Card>
                </>
              ) : (
                // Error or No Results
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    gap: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#666' }}>
                    {reportData.error || 'Nenhum resultado encontrado'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Tente reformular sua consulta
                  </Typography>
                </Box>
              )
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReportAI;
